import React from 'react';
import { Link } from 'react-router-dom';
import type { Shop, ShopOutcomeRow } from '../services/apiService';
import { statusWord, toneColour } from '../utils/shopPresentation';
import { shopHref } from '../utils/shopRef';

// ONE LINE PER SHOP. Name, one status word, two figures — and a link to the
// room that holds everything else.
//
// WHY TWO FIGURES AND WHICH TWO. Measured on the live account's 43 rows, the
// elements that actually vary are products (43 distinct), approval (42), EPC
// percentile (34) and payment days (27); `health warning` and `locks in window`
// were CONSTANT wherever they appeared (1 distinct value on 79% of rows), so by
// this screen's own scorer they carry no information on a line and belong in the
// room. `products` is chosen from that list, and `shows · clicks` is chosen
// AGAINST it: it varies less, but it is the only figure on the line about this
// publisher rather than about the shop's whole network, and the screen's job is
// "which of my feeds needs me". On the live account 20 of 29 feeds were shown and
// never clicked — rows that were indistinguishable before that figure existed.
//
// The 390px cap is two. The wide viewport shows the same two: a third column
// that only appears on desktop is a second layout to maintain for a figure the
// room already carries.
//
// WHICH COLUMNS IS THE CALLER'S, BECAUSE TWO OF THEM ARE DEAD ON `/feeds/add`.
// The picker lists shops that are `!supplied`, and `!supplied` implies no feed of
// their own — so `outcomeFor` returns null for every row it can ever render, and
// `statusWord` returns the constant "Not added". Measured on the live account:
// State 1 distinct value on 11 of 11 rows, Shown · clicked empty on 11 of 11,
// together 40% of the table's width at both viewports and, at 390px, 166px of
// horizontal scroll whose entire payload is a blank column.
//
// A shared table is still right — one row-rendering rule, one set of testids —
// but the column SET is a property of the question the screen asks, not of the
// component.

export type ShopColumn = 'state' | 'products' | 'catalogue' | 'outcome';

/** The hub's columns: everything, because on `/feeds` everything varies. */
export const HUB_COLUMNS: ShopColumn[] = ['state', 'products', 'outcome'];

// `products` and `catalogue` ARE DIFFERENT QUANTITIES AND USED TO SHARE A NAME.
//
// The hub's number is `productCount`: what this publisher HAS, imported and
// serving. The picker's is `programmeItemCount`: how big the shop's own
// catalogue is, which `shop-list.ts` marks "never presented as what the
// publisher will receive" — and a column headed `Products` presented it as
// exactly that, one screen away from the column where the word means the other
// thing. On the live account the picker's range is 2 to 422,024, and it is the
// only figure the picker offers to choose on.
//
// The header is short because it has to be: `.shop-table` cells are `nowrap`
// with `text-overflow: ellipsis`, and this column is 5rem.
const HEADING: Record<ShopColumn, string> = {
  state: 'State', products: 'Products', catalogue: 'Catalogue', outcome: 'Shown · clicked',
};

export interface ShopTableProps {
  shops: Shop[];
  /**
   * Only consulted when `columns` asks for `outcome`, so a caller that does not
   * omits it — which is what stops `/feeds/add` holding a dependency on a read it
   * cannot render.
   */
  outcomeFor?: (shop: Shop) => ShopOutcomeRow | null;
  /** Which figures follow the name. The name column is always first. */
  columns?: ShopColumn[];
  /** Rendered above the rows, for the group's own explanation. */
  caption?: React.ReactNode;
  testId: string;
}

const ShopTable: React.FC<ShopTableProps> = ({
  shops, outcomeFor = () => null, columns = HUB_COLUMNS, caption, testId,
}) => (
  <>
    {caption}
    {/* THE COLUMNS ARE THE SAME COLUMNS IN EVERY GROUP. Widths come from
        `.shop-table` in App.css (fixed layout, percentages) rather than from each
        table's own content, because the hub renders one table per group and two
        auto-laid tables compute independently — on the live account "Needs you"
        and "Running" did not line up, which reads as two unrelated lists rather
        than one list in two parts.

        Real `<th>`s rather than the caption that used to sit under the table:
        they name the figures where the figures are, they are announced with each
        cell, and they made the misalignment visible instead of implicit. */}
    <div className="shop-table-scroll">
    {/* The column COUNT is in the class because the floor that keeps four
        columns legible is the same floor that makes two scroll to nothing —
        see `.shop-table--cols-2` in App.css. */}
    <table className={`shop-table shop-table--cols-${columns.length + 1}`} data-testid={testId}>
      <thead>
        <tr>
          <th scope="col">Shop</th>
          {columns.map(c => (
            <th key={c} scope="col" className={c === 'state' ? undefined : 'shop-table-num'}>
              {HEADING[c]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {shops.map(shop => {
          const status = statusWord(shop);
          const outcome = outcomeFor(shop);
          const size = shop.productCount ?? shop.programmeItemCount;
          return (
            // The testid stays keyed on `shop.key`, not on the address: it is a
            // DOM identity for tests and the e2e suite, not a URL, and 40-odd
            // existing assertions depend on it.
            <tr key={shop.key} data-testid={`shop-${shop.key}`}>
              <td>
                {/* The whole row is one target — a name is a small tap area on a
                    phone, and there is exactly one destination per line. */}
                <Link to={shopHref(shop)} data-testid={`open-${shop.key}`} style={{ fontWeight: 600 }}>
                  {shop.label}
                </Link>
              </td>
              {columns.map(c => {
                if (c === 'state') return (
                  <td key={c} style={{ color: toneColour[status.tone] }} data-testid={`status-${shop.key}`}>
                    {status.text}
                  </td>
                );
                if (c === 'products' || c === 'catalogue') {
                  // Same slot, same testid — one number per row about how much
                  // there is. `catalogue` reads the programme's own count
                  // directly rather than through the fallback, because on the
                  // picker `productCount` is null by construction and the
                  // fallback would hide which quantity is being shown.
                  const n = c === 'catalogue' ? shop.programmeItemCount : size;
                  return (
                    <td key={c} className="shop-table-num" data-testid={`size-${shop.key}`}>
                      {n === null ? '' : n.toLocaleString()}
                    </td>
                  );
                }
                return (
                  <td key={c} className="shop-table-num" data-testid={`clicks-${shop.key}`}>
                    {/* An absence, never a zero. A shop with no feed of its own has
                        nothing to report, and "0" there reads as a verdict. */}
                    {outcome === null ? '' : `${outcome.shows.toLocaleString()} · ${outcome.clicks.toLocaleString()}`}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  </>
);

export default ShopTable;
